"use strict";
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { promiseWithResolversAndAbortCallback } from "#src/util/abort.js";
import { RefCounted } from "#src/util/disposable.js";
import { ProgressSpan } from "#src/util/progress_listener.js";
const IS_WORKER = !(typeof Window !== "undefined" && self instanceof Window);
const DEBUG = false;
const DEBUG_MESSAGES = false;
const PROMISE_RESPONSE_ID = "rpc.promise.response";
const PROMISE_CANCEL_ID = "rpc.promise.cancel";
const PROMISE_PROGRESS_ADD_SPAN_ID = "rpc.promise.addProgressSpan";
const PROMISE_PROGRESS_REMOVE_SPAN_ID = "rpc.promise.removeProgressSpan";
const READY_ID = "rpc.ready";
const handlers = /* @__PURE__ */ new Map();
export function registerRPC(key, handler) {
  handlers.set(key, handler);
}
class ProxyProgressListener {
  constructor(rpc, id) {
    this.rpc = rpc;
    this.id = id;
  }
  addSpan(span) {
    this.rpc.invoke(PROMISE_PROGRESS_ADD_SPAN_ID, {
      id: this.id,
      span: {
        id: span.id,
        message: span.message,
        startTime: span.startTime
      }
    });
  }
  removeSpan(spanId) {
    this.rpc.invoke(PROMISE_PROGRESS_REMOVE_SPAN_ID, {
      id: this.id,
      spanId
    });
  }
}
export function registerPromiseRPC(key, handler) {
  registerRPC(key, function(x) {
    const id = x.id;
    const abortController = new AbortController();
    let progressListener;
    if (x.progressListener === true) {
      progressListener = new ProxyProgressListener(this, id);
    }
    const promise = handler.call(this, x, {
      signal: abortController.signal,
      progressListener
    });
    this.set(id, { promise, abortController });
    promise.then(
      ({ value, transfers }) => {
        this.delete(id);
        this.invoke(PROMISE_RESPONSE_ID, { id, value }, transfers);
      },
      (error) => {
        this.delete(id);
        this.invoke(PROMISE_RESPONSE_ID, {
          id,
          error
        });
      }
    );
  });
}
registerRPC(PROMISE_CANCEL_ID, function(x) {
  const id = x.id;
  const request = this.get(id);
  if (request !== void 0) {
    const { abortController } = request;
    abortController.abort();
  }
});
registerRPC(PROMISE_RESPONSE_ID, function(x) {
  const id = x.id;
  const { resolve, reject } = this.get(id);
  this.delete(id);
  if (Object.prototype.hasOwnProperty.call(x, "value")) {
    resolve(x.value);
  } else {
    reject(x.error);
  }
});
registerRPC(PROMISE_PROGRESS_ADD_SPAN_ID, function(x) {
  const id = x.id;
  const { progressListener } = this.get(id);
  new ProgressSpan(progressListener, x.span);
});
registerRPC(PROMISE_PROGRESS_REMOVE_SPAN_ID, function(x) {
  const id = x.id;
  const { progressListener } = this.get(id);
  progressListener.removeSpan(x.spanId);
});
registerRPC(READY_ID, function(x) {
  x;
  this.onPeerReady();
});
const INITIAL_RPC_ID = IS_WORKER ? -1 : 0;
export class RPC {
  constructor(target, waitUntilReady) {
    this.target = target;
    if (waitUntilReady) {
      this.queue = [];
    }
    target.onmessage = (e) => {
      const data = e.data;
      if (DEBUG_MESSAGES) {
        console.log("Received message", data);
      }
      const handler = handlers.get(data.functionName);
      if (handler === void 0) {
        throw new Error(`Missing RPC function: ${data.functionName}`);
      }
      handlers.get(data.functionName).call(this, data);
    };
  }
  objects = /* @__PURE__ */ new Map();
  nextId = INITIAL_RPC_ID;
  queue;
  sendReady() {
    this.invoke(READY_ID, {});
  }
  onPeerReady() {
    const { queue } = this;
    if (queue === void 0) return;
    this.queue = void 0;
    for (const { data, transfers } of queue) {
      this.target.postMessage(data, transfers);
    }
  }
  get numObjects() {
    return this.objects.size;
  }
  set(id, value) {
    this.objects.set(id, value);
  }
  delete(id) {
    this.objects.delete(id);
  }
  get(id) {
    return this.objects.get(id);
  }
  getRef(x) {
    const rpcId = x.id;
    const obj = this.get(rpcId);
    obj.referencedGeneration = x.gen;
    obj.addRef();
    return obj;
  }
  getOptionalRef(x) {
    if (x === void 0) return void 0;
    const rpcId = x.id;
    const obj = this.get(rpcId);
    obj.referencedGeneration = x.gen;
    obj.addRef();
    return obj;
  }
  invoke(name, x, transfers) {
    x.functionName = name;
    if (DEBUG_MESSAGES) {
      console.trace("Sending message", x);
    }
    const { queue } = this;
    if (queue !== void 0) {
      queue.push({ data: x, transfers });
      return;
    }
    this.target.postMessage(x, transfers);
  }
  promiseInvoke(name, x, options) {
    let signal;
    let progressListener;
    let transfers;
    if (options !== void 0) {
      ({ signal, progressListener, transfers } = options);
    }
    if (signal?.aborted) {
      return Promise.reject(signal.reason);
    }
    if (progressListener !== void 0) {
      x.progressListener = true;
    }
    const id = x.id = this.newId();
    this.invoke(name, x, transfers);
    const { promise, resolve, reject } = signal === void 0 ? Promise.withResolvers() : promiseWithResolversAndAbortCallback(signal, () => {
      this.invoke(PROMISE_CANCEL_ID, { id });
    });
    this.set(id, { resolve, reject, progressListener });
    return promise;
  }
  newId() {
    return IS_WORKER ? this.nextId-- : this.nextId++;
  }
}
export class SharedObject extends RefCounted {
  rpc = null;
  rpcId = null;
  isOwner;
  unreferencedGeneration;
  referencedGeneration;
  initializeSharedObject(rpc, rpcId = rpc.newId()) {
    this.rpc = rpc;
    this.rpcId = rpcId;
    this.isOwner = false;
    rpc.set(rpcId, this);
  }
  initializeCounterpart(rpc, options = {}) {
    this.initializeSharedObject(rpc);
    this.unreferencedGeneration = 0;
    this.referencedGeneration = 0;
    this.isOwner = true;
    options.id = this.rpcId;
    options.type = this.RPC_TYPE_ID;
    rpc.invoke("SharedObject.new", options);
  }
  dispose() {
    super.dispose();
  }
  /**
   * Precondition: this.isOwner === true.
   */
  addCounterpartRef() {
    return { id: this.rpcId, gen: ++this.referencedGeneration };
  }
  refCountReachedZero() {
    if (this.isOwner === true) {
      if (this.referencedGeneration === this.unreferencedGeneration) {
        this.ownerDispose();
      }
    } else if (this.isOwner === false) {
      this.rpc.invoke("SharedObject.refCountReachedZero", {
        id: this.rpcId,
        gen: this.referencedGeneration
      });
    } else {
      super.refCountReachedZero();
    }
  }
  /**
   * Precondition: this.isOwner === true.
   */
  ownerDispose() {
    if (DEBUG) {
      console.log(`[${IS_WORKER}] #rpc object = ${this.rpc.numObjects}`);
    }
    const { rpc, rpcId } = this;
    super.refCountReachedZero();
    rpc.delete(rpcId);
    rpc.invoke("SharedObject.dispose", { id: rpcId });
  }
  /**
   * Precondition: this.isOwner === true.
   *
   * This should be called when the counterpart's refCount is decremented and reaches zero.
   */
  counterpartRefCountReachedZero(generation) {
    this.unreferencedGeneration = generation;
    if (this.refCount === 0 && generation === this.referencedGeneration) {
      this.ownerDispose();
    }
  }
}
export function initializeSharedObjectCounterpart(obj, rpc, options = {}) {
  if (rpc != null) {
    obj.initializeSharedObject(rpc, options.id);
  }
}
export class SharedObjectCounterpart extends SharedObject {
  constructor(rpc, options = {}) {
    super();
    initializeSharedObjectCounterpart(this, rpc, options);
  }
}
registerRPC("SharedObject.dispose", function(x) {
  const obj = this.get(x.id);
  if (obj.refCount !== 0) {
    throw new Error(
      "Attempted to dispose object with non-zero reference count."
    );
  }
  if (DEBUG) {
    console.log(`[${IS_WORKER}] #rpc objects: ${this.numObjects}`);
  }
  obj.disposed();
  this.delete(obj.rpcId);
  obj.rpcId = null;
  obj.rpc = null;
});
registerRPC("SharedObject.refCountReachedZero", function(x) {
  const obj = this.get(x.id);
  const generation = x.gen;
  obj.counterpartRefCountReachedZero(generation);
});
const sharedObjectConstructors = /* @__PURE__ */ new Map();
export function registerSharedObjectOwner(identifier) {
  return (constructorFunction) => {
    constructorFunction.prototype.RPC_TYPE_ID = identifier;
  };
}
export function registerSharedObject(identifier) {
  return (constructorFunction) => {
    if (identifier !== void 0) {
      constructorFunction.prototype.RPC_TYPE_ID = identifier;
    } else {
      identifier = constructorFunction.prototype.RPC_TYPE_ID;
      if (identifier === void 0) {
        throw new Error("RPC_TYPE_ID should have already been defined");
      }
    }
    sharedObjectConstructors.set(identifier, constructorFunction);
  };
}
registerRPC("SharedObject.new", function(x) {
  const rpc = this;
  const typeName = x.type;
  const constructorFunction = sharedObjectConstructors.get(typeName);
  const obj = new constructorFunction(rpc, x);
  --obj.refCount;
});
